import axios from "axios";

// ─────────────────────────────────────────────
//  INSTANCE AXIOS
//
//  baseURL relative ("/api") plutôt qu'une IP:port en dur.
//  Le navigateur appelle alors le même domaine que celui utilisé
//  pour charger la page (deskoraadmin.cpucsolutions.com), et nginx
//  se charge de router /api vers le backend Spring Boot en interne.
//  Ça élimine aussi tout souci CORS (même origine) et fonctionne
//  aussi bien en local qu'en accès distant via le reverse proxy.
// ─────────────────────────────────────────────

const api = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
    "Accept":       "application/json",
  },
  timeout: 10000,
});


// ─────────────────────────────────────────────
//  INTERCEPTOR REQUÊTE
//  Injecte automatiquement le token JWT dans chaque appel
// ─────────────────────────────────────────────

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});


// ─────────────────────────────────────────────
//  INTERCEPTOR RÉPONSE — REFRESH AUTOMATIQUE
//
//  Si le serveur répond 401 (token expiré) :
//    1. On appelle POST /users/refresh-token
//    2. On sauvegarde le nouveau token
//    3. On relance la requête originale automatiquement
//
//  Si plusieurs requêtes expirent en même temps :
//    → une seule fait le refresh, les autres attendent en file
//
//  Si le refresh échoue aussi :
//    → on vide le token et on notifie AuthContext via un événement
// ─────────────────────────────────────────────

// Empêche de lancer plusieurs refresh en parallèle
let isRefreshing = false;

// File d'attente des requêtes bloquées pendant le refresh
let pendingRequests = [];

const retryPendingRequests = (newToken) => {
  pendingRequests.forEach(({ resolve }) => resolve(newToken));
  pendingRequests = [];
};

const rejectPendingRequests = (error) => {
  pendingRequests.forEach(({ reject }) => reject(error));
  pendingRequests = [];
};


api.interceptors.response.use(
  // Réponse normale → on la laisse passer
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    const is401          = error.response?.status === 401;
    const isRefreshRoute = originalRequest.url?.includes("/users/refresh-token");
    const alreadyRetried = originalRequest._retry;

    if (is401 && !isRefreshRoute && !alreadyRetried) {
      originalRequest._retry = true;

      // Un refresh est déjà en cours → on met la requête en attente
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        // Demande un nouveau token au backend
        const response = await api.post("/users/refresh-token");
        const newToken = response.data.data;

        // Sauvegarde le nouveau token
        localStorage.setItem("token", newToken);

        // Relance toutes les requêtes en attente
        retryPendingRequests(newToken);

        // Relance la requête originale
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);

      } catch (refreshError) {
        // Refresh échoué → session terminée
        rejectPendingRequests(refreshError);
        localStorage.removeItem("token");

        // Notifie AuthContext pour déconnecter l'utilisateur
        window.dispatchEvent(new Event("auth:session-expired"));

        return Promise.reject(refreshError);

      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);


export default api;